/**
 * Press quote slider — owl-style loop, 3 visible, one active (center only).
 */
(function () {
  'use strict';

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    const quoteEl = root.querySelector('[data-press-quote-text]');
    const starsEl = root.querySelector('[data-press-quote-stars]');
    const viewport = root.querySelector('[data-press-viewport]');
    const track = root.querySelector('[data-press-track]');
    if (!quoteEl || !viewport || !track) return;

    const originals = Array.from(track.querySelectorAll('[data-press-slide]:not([data-press-clone])'));
    const count = originals.length;
    if (!count) return;

    const configuredDefault = parseInt(root.dataset.defaultIndex, 10);
    let logicalIndex = Number.isNaN(configuredDefault)
      ? 0
      : Math.min(Math.max(configuredDefault, 0), count - 1);

    let autoplayTimer = null;
    let isJumping = false;
    let isAnimating = false;
    let scrollEndTimer = null;
    let allSlides = originals;

    const mod = (n) => ((n % count) + count) % count;

    const logicalFromSlide = (slide) => {
      if (slide.dataset.realIndex !== undefined) {
        return parseInt(slide.dataset.realIndex, 10);
      }
      const i = allSlides.indexOf(slide);
      return mod(i - count);
    };

    const middleSlide = (logical) => originals[mod(logical)];

    const prependSlide = (logical) => (count < 2 ? originals[mod(logical)] : allSlides[mod(logical)]);

    const appendSlide = (logical) =>
      count < 2 ? originals[mod(logical)] : allSlides[count * 2 + mod(logical)];

    const slideZone = (slide) => {
      if (count < 2) return 'middle';
      const idx = allSlides.indexOf(slide);
      if (idx < count) return 'prepend';
      if (idx >= count * 2) return 'append';
      return 'middle';
    };

    const setQuote = (text, rating) => {
      const stars = Math.min(5, Math.max(1, parseInt(rating, 10) || 5));
      const starStr = '★'.repeat(stars);
      const quoteChanged = text && quoteEl.textContent !== text;
      const starsChanged = starsEl && starsEl.textContent !== starStr;

      if (!quoteChanged && !starsChanged) return;

      quoteEl.classList.add('is-changing');
      if (starsEl) starsEl.classList.add('is-changing');

      window.setTimeout(() => {
        if (text) quoteEl.textContent = text;
        if (starsEl) starsEl.textContent = starStr;
        quoteEl.classList.remove('is-changing');
        if (starsEl) starsEl.classList.remove('is-changing');
      }, prefersReduced ? 0 : 150);
    };

    /** Exactly one visible active slide — the one at viewport center (never all clones). */
    const setActiveFromViewport = () => {
      const center = viewport.scrollLeft + viewport.clientWidth / 2;
      let nearest = allSlides[0];
      let bestDist = Infinity;

      allSlides.forEach((slide) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(slideCenter - center);
        if (dist < bestDist) {
          bestDist = dist;
          nearest = slide;
        }
      });

      logicalIndex = logicalFromSlide(nearest);

      allSlides.forEach((slide) => {
        const isCentered = slide === nearest;
        slide.classList.toggle('is-active', isCentered);
      });

      originals.forEach((slide, i) => {
        slide.setAttribute('aria-pressed', i === logicalIndex ? 'true' : 'false');
      });

      setQuote(originals[logicalIndex].dataset.quote || '', originals[logicalIndex].dataset.rating);
      return nearest;
    };

    const scrollToSlide = (slide, smooth) => {
      if (!slide) return;
      const left = slide.offsetLeft - (viewport.clientWidth - slide.offsetWidth) / 2;
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const useSmooth = smooth && !prefersReduced;

      if (useSmooth) isAnimating = true;

      viewport.style.scrollSnapType = useSmooth ? 'none' : '';
      viewport.scrollTo({
        left: Math.min(Math.max(0, left), max),
        behavior: useSmooth ? 'smooth' : 'auto',
      });

      window.clearTimeout(scrollEndTimer);
      let finished = false;
      const onDone = () => {
        if (finished) return;
        finished = true;
        window.clearTimeout(scrollEndTimer);
        isAnimating = false;
        viewport.style.scrollSnapType = '';
        if (!isJumping) normalizeLoop();
        if (!isJumping) setActiveFromViewport();
      };

      if (useSmooth) {
        scrollEndTimer = window.setTimeout(onDone, 500);
        if ('onscrollend' in viewport) {
          viewport.addEventListener('scrollend', onDone, { once: true });
        }
      } else {
        onDone();
      }
    };

    const normalizeLoop = () => {
      if (isJumping || count < 2) return;

      const nearest = setActiveFromViewport();
      const zone = slideZone(nearest);

      if (zone === 'middle') return;

      isJumping = true;
      const logical = logicalFromSlide(nearest);
      scrollToSlide(middleSlide(logical), false);
      isJumping = false;
      setActiveFromViewport();
    };

    const scrollToLogical = (logical, smooth) => {
      const targetLogical = mod(logical);
      const target = middleSlide(targetLogical);

      if (count < 2) {
        scrollToSlide(target, smooth);
        return;
      }

      const nearest = setActiveFromViewport();
      const current = logicalFromSlide(nearest);
      const zone = slideZone(nearest);

      if (!smooth || prefersReduced) {
        scrollToSlide(target, false);
        return;
      }

      if (zone !== 'middle') {
        isJumping = true;
        scrollToSlide(middleSlide(current), false);
        isJumping = false;
      }

      const stepsForward = mod(targetLogical - current);
      const stepsBack = mod(current - targetLogical);
      let animSlide = target;

      if (stepsForward === 1 && current === count - 1 && targetLogical === 0) {
        animSlide = appendSlide(0);
      } else if (stepsBack === 1 && current === 0 && targetLogical === count - 1) {
        animSlide = prependSlide(count - 1);
      }

      scrollToSlide(animSlide, true);
    };

    const buildLoop = () => {
      if (count < 2) return;

      originals.forEach((slide, i) => {
        slide.dataset.realIndex = String(i);
      });

      const prependFrag = document.createDocumentFragment();
      const appendFrag = document.createDocumentFragment();

      originals.forEach((slide, i) => {
        const before = slide.cloneNode(true);
        before.setAttribute('data-press-clone', 'prepend');
        before.setAttribute('data-real-index', String(i));
        before.setAttribute('aria-hidden', 'true');
        before.setAttribute('tabindex', '-1');
        before.classList.remove('is-active');
        prependFrag.appendChild(before);
      });

      originals.forEach((slide, i) => {
        const after = slide.cloneNode(true);
        after.setAttribute('data-press-clone', 'append');
        after.setAttribute('data-real-index', String(i));
        after.setAttribute('aria-hidden', 'true');
        after.setAttribute('tabindex', '-1');
        after.classList.remove('is-active');
        appendFrag.appendChild(after);
      });

      track.insertBefore(prependFrag, track.firstChild);
      track.appendChild(appendFrag);
      allSlides = Array.from(track.querySelectorAll('[data-press-slide]'));
    };

    const updateSlideMetrics = () => {
      const gap = parseFloat(getComputedStyle(track).gap) || 48;
      const slideW = Math.min(200, Math.max(110, (viewport.clientWidth - gap * 2) / 3));
      root.style.setProperty('--press-slide-size', `${slideW}px`);
      root.style.setProperty('--press-slide-gap', `${gap}px`);
    };

    buildLoop();
    updateSlideMetrics();

    allSlides.forEach((slide) => {
      slide.addEventListener('click', () => {
        scrollToLogical(logicalFromSlide(slide), true);
        resetAutoplay();
      });
    });

    let scrollTicking = false;
    viewport.addEventListener(
      'scroll',
      () => {
        if (scrollTicking || isJumping) return;
        scrollTicking = true;
        requestAnimationFrame(() => {
          if (!isAnimating) setActiveFromViewport();
          scrollTicking = false;
        });
      },
      { passive: true }
    );

    const onScrollSettle = () => {
      if (isJumping || isAnimating) return;
      normalizeLoop();
    };

    if ('onscrollend' in viewport) {
      viewport.addEventListener('scrollend', onScrollSettle, { passive: true });
    }

    const startAutoplay = () => {
      if (prefersReduced || root.dataset.autoplay !== 'true' || count < 2) return;
      const sec = parseInt(root.dataset.autoplayInterval, 10) || 5;
      stopAutoplay();
      autoplayTimer = window.setInterval(() => {
        scrollToLogical(logicalIndex + 1, true);
      }, sec * 1000);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const resetAutoplay = () => {
      stopAutoplay();
      startAutoplay();
    };

    window.addEventListener(
      'resize',
      () => {
        updateSlideMetrics();
        if (!isAnimating) scrollToSlide(middleSlide(logicalIndex), false);
      },
      { passive: true }
    );

    requestAnimationFrame(() => {
      updateSlideMetrics();
      scrollToSlide(middleSlide(logicalIndex), false);
      setActiveFromViewport();
    });

    startAutoplay();
    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', startAutoplay);
  }

  function boot() {
    document.querySelectorAll('[data-press-slider]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
